import React, { useState, useEffect, startTransition } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Box,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { HttpResponse, HTTPValidationError, Project, ProjectCreate } from 'src/api/Api';
import clientService from 'src/clientService';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { RegistryList, ProjectTypeList } from 'src/dropdown-config';
// import { calculateCreditingPeriod } from 'src/util/util';
import { useAppState } from 'src/state-management/state-provider';
import { countryList } from 'src/constants/countryList';

// Import SDG images
import SDG1 from 'src/resources/images/SDG1.png';
import SDG2 from 'src/resources/images/SDG2.png';
import SDG3 from 'src/resources/images/SDG5.png'; // Corresponds to the 3rd SDG image slot in ProjectDetailComponent
import SDG4 from 'src/resources/images/SDG8.png'; // Corresponds to the 4th SDG image slot
import SDG5 from 'src/resources/images/SDG13.png';
import SDG6 from 'src/resources/images/SDG14.png';
import SDG7 from 'src/resources/images/SDG15.png';

const initalState: ProjectCreate = {
  project_name: '',
  project_type: '',
  has_carbon_module: false,
  has_biodiversity_module: false,
  has_environmental_module: false,
  project_developer: '',
  sdg: [], // Initialize sdg as an empty array
};

// Define an array for SDG options
const sdgOptionsList = [
  { id: 'SDG1', src: SDG1, alt: 'SDG 1: No Poverty' },
  { id: 'SDG2', src: SDG2, alt: 'SDG 2: Zero Hunger' },
  { id: 'SDG5', src: SDG3, alt: 'SDG 5: Good Health and Well-being' },
  { id: 'SDG8', src: SDG4, alt: 'SDG 8: Quality Education' },
  { id: 'SDG13', src: SDG5, alt: 'SDG 13: Gender Equality' },
  { id: 'SDG14', src: SDG6, alt: 'SDG 14: Clean Water and Sanitation' },
  { id: 'SDG15', src: SDG7, alt: 'SDG 15: Affordable and Clean Energy' },
];

type ProjectDialogProps = {
  open: boolean;
  onClose: () => void;
  project?: Project;
};

const ProjectDialog = ({ open, onClose, project }: ProjectDialogProps) => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();

  const [formData, setFormData] = useState<ProjectCreate | Partial<Project>>(initalState);

  useEffect(() => {
    if (project) {
      setFormData({ ...project, sdg: project.sdg || [] }); // Initialize sdg from project or empty array
    } else {
      // When creating a new project, or when project is undefined, reset to initial state
      setFormData({ ...initalState });
    }
  }, [project, open]); // Ensure dialog reset when 'open' changes

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSdgSelection = (sdgId: string) => {
    setFormData((prev) => {
      const currentSdgs = Array.isArray(prev.sdg) ? prev.sdg : [];
      const newSdgs = currentSdgs.includes(sdgId)
        ? currentSdgs.filter((s) => s !== sdgId)
        : [...currentSdgs, sdgId];
      return { ...prev, sdg: newSdgs };
    });
  };

  const validateProjectDates = (): boolean => {
    if (!formData.project_start_date || !formData.project_end_date) {
      return true;
    }

    const startDate = new Date(formData.project_start_date as string);
    const endDate = new Date(formData.project_end_date as string);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      showSnackbar({
        message: 'Invalid format for project start or end date.',
        type: snackbarType.ERROR,
      });
      return false;
    }

    if (endDate < startDate) {
      showSnackbar({
        message: 'Project end date must be equal to or later than the start date.',
        type: snackbarType.ERROR,
      });
      return false;
    }

    return true;
  };

  const duplicateProjectName = (name: string) => {
    return state.projects.some((p) => p.project_name.toLowerCase() === name.toLowerCase());
  };
  const handleSubmit = () => {
    const anyAnalysisType =
      formData.has_carbon_module ||
      formData.has_biodiversity_module ||
      formData.has_environmental_module;
    if (!formData.project_name || !formData.project_developer || !anyAnalysisType) {
      showSnackbar({
        message: 'Please fill in all mandatory fields.',
        type: snackbarType.ERROR,
      });
      return;
    }
    
    if (!project && duplicateProjectName(formData.project_name)) {
      showSnackbar({
        message: 'Project name already exists. Please choose a different name.',
        type: snackbarType.ERROR,
      });
      return;
    }

    // Validate project dates if both are provided
    if (!validateProjectDates()) {
    return;
    }
    // Map analysis_type back to boolean fields if your API expects them

    if (project) {
      // Update existing project

      // Validate credit period dates if both are provided
      // if (formData.project_start_date && formData.project_end_date) {
      //   const startDate = new Date(formData.project_start_date as string);
      //   const endDate = new Date(formData.project_end_date as string);

      //   if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      //     showSnackbar({
      //       message: 'Invalid format for credit period start or end date.',
      //       type: snackbarType.ERROR,
      //     });
      //     return;
      //   }

      //   if (endDate <= startDate) {
      //     showSnackbar({
      //       message: 'Credit period end date must be after the start date.',
      //       type: snackbarType.ERROR,
      //     });
      //     return;
      //   }
      // }

      const updatePayload = { ...formData } as ProjectCreate;

      // if (formData.project_start_date && formData.project_end_date) {
      //   // At this point, dates are valid and endDate > startDate
      //   const creditingPeriod = calculateCreditingPeriod(formData.project_start_date as string, formData.project_end_date as string);
      //   if (creditingPeriod) { // creditingPeriod should be a string here
      //     updatePayload = { ...updatePayload, crediting_period: creditingPeriod };
      //   }
      // }

      clientService.api
        .updateProjectApiV1ProjectsProjectIdPut(project.project_id, updatePayload as ProjectCreate)
        .then((updatedProject: HttpResponse<Project, HTTPValidationError>) => {
          dispatch({ type: 'SET_PROJECT_DETAILS', payload: updatedProject.data });
          showSnackbar({ message: 'Project updated successfully!' });
          setFormData(updatedProject.data);
        })
        .catch((error) => {
          console.error('Error updating project:', error);
          showSnackbar({
            message:
              'Error updating project:' +
              (error.error?.detail || error.message || 'Unknown error.'),
            type: snackbarType.ERROR,
          });
        });
    } else {
      // Create new project
      clientService.api
        .createProjectApiV1ProjectsPost(formData as ProjectCreate)
        .then((newProject: HttpResponse<Project, HTTPValidationError>) => {
          startTransition(() => {
            dispatch({ type: 'SET_PROJECT_LIST', payload: [newProject.data, ...state.projects] });
          });
          setFormData(initalState);
          showSnackbar({ message: 'Project added successfully!' });
        })
        .catch(() => {
          showSnackbar({
            message: 'Error adding site:',
            type: snackbarType.ERROR,
          });
        });
    }
    // onSubmit(formData);
    onClose();
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box p={3}>
        <DialogTitle>
          {project ? 'Update your project details' : 'Create New Project'}
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {project && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 'medium' }}>
                Sustainable Development Goals (SDGs)
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  mb: 2,
                  justifyContent: 'flex-start',
                }}
              >
                {sdgOptionsList.map((sdg) => (
                  <Box
                    key={sdg.id}
                    component="img"
                    src={sdg.src}
                    alt={sdg.alt}
                    title={sdg.alt} // Tooltip on hover
                    onClick={() => handleSdgSelection(sdg.id)}
                    sx={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: formData.sdg?.includes(sdg.id)
                        ? '3px solid black'
                        : '3px solid #e0e0e0',
                      padding: '2px',
                      backgroundColor: formData.sdg?.includes(sdg.id) ? '#e8f5e9' : '#fff',
                      '&:hover': {
                        borderColor: formData.sdg?.includes(sdg.id) ? 'black' : '#bdbdbd',
                        transform: 'scale(1.05)',
                      },
                      transition:
                        'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out, transform 0.2s ease-in-out',
                    }}
                  />
                ))}
              </Box>
            </>
          )}

          <TextField
            label="Project Name"
            value={formData.project_name || ''}
            onChange={(e) => handleChange('project_name', e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={!!project} // Disable editing for existing projects
          />
          <TextField
            label="Project Developer"
            value={formData.project_developer || ''}
            onChange={(e) => handleChange('project_developer', e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={!!project} // Disable editing for existing projects
          />
          <TextField
            label="Project Start Date"
            type="date"
            value={formData.project_start_date || ''}
            onChange={(e) => handleChange('project_start_date', e.target.value)}
            fullWidth
            margin="normal"
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />

          <TextField
            label="Project End Date"
            type="date"
            value={formData.project_end_date || ''}
            onChange={(e) => handleChange('project_end_date', e.target.value)}
            fullWidth
            margin="normal"
            slotProps={{
              inputLabel: { shrink: true },
            }}
            inputProps={{
              min: formData.project_start_date
                ? (formData.project_start_date as string).split('T')[0]
                : undefined,
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.has_carbon_module}
                onChange={handleCheckboxChange}
                name="has_carbon_module"
              />
            }
            label="Carbon Analysis"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.has_biodiversity_module}
                onChange={handleCheckboxChange}
                name="has_biodiversity_module"
              />
            }
            label="Biodiversity Analysis"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.has_environmental_module}
                onChange={handleCheckboxChange}
                name="has_environmental_module"
              />
            }
            label="Climate Analysis"
          />
          {formData.has_carbon_module && (
            <TextField
              label="Carbon Registry"
              value={formData.registry || ''}
              onChange={(e) => handleChange('registry', e.target.value)}
              select
              fullWidth
              margin="normal"
            >
              {RegistryList.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {formData.has_carbon_module && (
            <TextField
              label="Project Type"
              value={formData.project_type || ''}
              onChange={(e) => handleChange('project_type', e.target.value)}
              select
              fullWidth
              margin="normal"
            >
              {formData.registry &&
                ProjectTypeList[formData.registry].map((child) => (
                  <MenuItem key={child.value} value={child.value}>
                    {child.name}
                  </MenuItem>
                ))}
            </TextField>
          )}
          {project && (
            <>
              <Autocomplete
                id="country-select-demo"
                options={countryList}
                autoHighlight
                getOptionLabel={(option) => option.label}
                value={countryList.find((option) => option.countryCode2 === formData.country)}
                onChange={(event, newValue) => {
                  handleChange('country', newValue?.countryCode2);
                }}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box
                      key={key}
                      component="li"
                      sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                      {...optionProps}
                    >
                      <img
                        loading="lazy"
                        width="20"
                        srcSet={`https://flagcdn.com/w40/${option.countryCode2.toLowerCase()}.png 2x`}
                        src={`https://flagcdn.com/w20/${option.countryCode2.toLowerCase()}.png`}
                        alt=""
                      />
                      {option.label} ({option.countryCode2})
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Choose a country"
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        autoComplete: 'new-password', // disable autocomplete and autofill
                      },
                    }}
                  />
                )}
              />
              <TextField
                label="Project Description"
                value={formData.project_description || ''}
                onChange={(e) => handleChange('project_description', e.target.value)}
                fullWidth
                margin="normal"
                multiline
                rows={7}
              />
              {/* <Box display="flex" justifyContent="space-between" gap={1}>
                <TextField
                  label="Credit Period Start"
                  type="date"
                  value={formData.project_start_date || ''}
                  onChange={(e) => handleChange('project_start_date', e.target.value)}
                  fullWidth // Added for better alignment if needed, or adjust Box spacing
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: formData.project_end_date ? (formData.project_end_date as string).split('T')[0] : undefined,
                  }}
                />
                <TextField
                  label="Credit Period End"
                  type="date"
                  value={formData.project_end_date || ''}
                  onChange={(e) => handleChange('project_end_date', e.target.value)}
                  fullWidth // Added for better alignment if needed, or adjust Box spacing
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: formData.project_start_date ? (formData.project_start_date as string).split('T')[0] : undefined,
                  }}
                />
              </Box> */}
              {/* Add more editable fields for existing projects here */}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ProjectDialog;
